var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
const debug = false;
export class Blackmagic {
    // other class properties and constructor
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.inputLabels = [];
        this.outputLabels = [];
        this.numVideoInputs = 16;
        this.numVideoOutputs = 12;
        this.preambleFirmwareInfo = undefined;
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const videoOutput = parseInt(key, 10);
                const videoInput = parseInt(value, 10);
                const match = yield this.socket.send(`VIDEO OUTPUT ROUTING:\n${videoOutput} ${videoInput}`, /ACK/i, tags);
                return match ? value : this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                regExp: /VIDEO OUTPUT ROUTING:\n([\s\S]+)/i,
                callback: (match) => {
                    this.getMatrixRoutes(match[1]);
                },
            },
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.socket.send(`PING:`, /ACK/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            if (this.preambleFirmwareInfo)
                this.state.firmwareVersion.update(this.preambleFirmwareInfo);
            // Get the number of inputs and outputs.
            const match = yield this.socket.send('VIDEOHUB DEVICE:', /Video inputs:\s+(\d+)\nVideo outputs:\s+(\d+)/i, ['channels']);
            if (match) {
                this.numVideoInputs = parseInt(match[1], 10);
                this.numVideoOutputs = parseInt(match[2], 10);
            }
            if (debug) {
                this.log.debug(`num inputs/outputs: ${this.numVideoInputs} x ${this.numVideoOutputs}`);
            }
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            // Get initial routing as array of key values separated by '\n'.
            const matrixRoutes = yield this.socket.send('VIDEO OUTPUT ROUTING:', /VIDEO OUTPUT ROUTING:\n([\s\S]+)/i, ['routing']);
            if (matrixRoutes) {
                this.getMatrixRoutes(matrixRoutes[1]);
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            let connectStatus = 'verifyFailed';
            // When the device connects, it sends status information including the firmware version and
            // input/output labels.
            const detectTimeout = this.timeouts.create();
            const fwVersionResp = this.socket.registerAsync(/Version:\s+([\d\.]+)/i, (match) => {
                if (debug) {
                    this.log.debug(`found firmware: ${match[1]}`);
                }
                this.preambleFirmwareInfo = match[1];
                connectStatus = 'connected';
                detectTimeout.restart(1000);
            });
            const inputLabelsResp = this.socket.registerAsync(/INPUT LABELS:\s+([\s\S]+)/i, (match) => {
                if (debug) {
                    this.log.debug(`found input labels: ${match[1]}`);
                }
                this.getLabels(this.inputLabels, match[1]);
                connectStatus = 'connected';
                detectTimeout.restart(1000);
            });
            const outputLabelsResp = this.socket.registerAsync(/OUTPUT LABELS:\s+([\s\S]+)/i, (match) => {
                if (debug) {
                    this.log.debug(`found output labels: ${match[1]}`);
                }
                this.getLabels(this.outputLabels, match[1]);
                connectStatus = 'connected';
                detectTimeout.restart(1000);
            });
            detectTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                this.socket.unregisterAsync(fwVersionResp);
                this.socket.unregisterAsync(inputLabelsResp);
                this.socket.unregisterAsync(outputLabelsResp);
            }), 5000);
            yield this.timeouts.delay(5000); // wait for initialization values from the device
            // If the status information isn't present, confirm device with a PING: command.
            if (connectStatus === 'verifyFailed') {
                const match = yield this.socket.send('PING:', /ACK/i, ['initialize', 'ping']);
                if (match) {
                    connectStatus = 'connected';
                }
            }
            return connectStatus;
        });
    }
    // other class methods
    getDetected() {
        var _a, _b;
        const videoInputs = [];
        const videoOutputs = [];
        for (let i = 1; i <= this.numVideoInputs; i++) {
            const name = (_a = this.inputLabels[i - 1]) !== null && _a !== void 0 ? _a : `Input ${i}`;
            videoInputs.push({ code: `${i - 1}`, external: true, name });
        }
        for (let i = 1; i <= this.numVideoOutputs; i++) {
            const name = (_b = this.outputLabels[i - 1]) !== null && _b !== void 0 ? _b : `Output ${i}`;
            videoOutputs.push({ code: `${i - 1}`, name });
        }
        if (debug) {
            this.log.debug(`Detected inputs = ${JSON.stringify(videoInputs, null, 4)}`);
            this.log.debug(`Detected outputs = ${JSON.stringify(videoOutputs, null, 4)}`);
        }
        return { videoInputs, videoOutputs };
    }
    getLabels(labelArray, labels) {
        const labelInfo = labels.split('\n');
        if (labelInfo.length > 0) {
            const labelRegex = /(\d+)\s+(.+)/; // format is: outputNum label
            labelInfo.forEach(label => {
                const match = labelRegex.exec(label);
                if (match) {
                    const key = parseInt(match[1], 10);
                    labelArray[key] = match[2];
                    if (debug) {
                        this.log.debug(`updateLabels match: ${key}: ${match[2]}`);
                    }
                }
            });
            if (debug) {
                this.log.debug(`labels = ${JSON.stringify(labelArray)}`);
            }
        }
    }
    getMatrixRoutes(matrixRoutes) {
        const matrixRoutesParse = matrixRoutes.split('\n');
        if (matrixRoutesParse.length > 0) {
            matrixRoutesParse.forEach(route => {
                const out_in = route.split(' '); // format is: output input
                if (debug) {
                    this.log.debug(`updating route: ${out_in[0]} -> ${out_in[1]}`);
                }
                this.state.videoMatrixRoutes.update(`${out_in[0]}`, `${out_in[1]}`);
            });
        }
    }
}
// static properties
Blackmagic.driverType = 'socketDriver';
Blackmagic.key = '-OIGDNOEHC2BNTnhzEjj';
Blackmagic.socketOptions = [
    {
        // The Blackmagic ATEM has a multi-line status response that is more convenient to
        // handle as a single command rather than processing line-by-line. This command
        // extractor extracts \n\n terminated commands.
        commandExtractor: (buffer) => {
            let command = '';
            for (let i = 0; i < buffer.length - 1; i++) {
                const c = buffer.charAt(i);
                const nextc = buffer.charAt(i + 1);
                if (c === '\n' && nextc === '\n') {
                    return { command, buffer: buffer.slice(i + 2) };
                }
                else {
                    command += c;
                }
            }
            return undefined;
        },
        sendTerminator: '\n\n',
        port: 9990,
    },
];
